import { Button, Flex, Popconfirm, Tag, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import type { LeadMatch } from "../../../Leads/api/services.types.ts"
import { LeadWizardMatchPanel } from "./LeadWizardMatchPanel.tsx"
import {
  REASON_COLOUR,
  REASON_LABEL,
  isReachable,
  matchAgeLabel,
  matchStateLabel
} from "./lead-match-vocabulary.ts"
import { MAX_VISIBLE_LEAD_MATCHES } from "./use-lead-matches.ts"

interface LeadWizardLeadWarningProps {
  matches: LeadMatch[]
  onOpenExisting: (match: LeadMatch) => void
  onCreateAnyway: () => void
}

/**
 * Sits above the customer warning. Open Existing Lead discards the form, where
 * the customer panel's Use Existing keeps it — so the two must never share a
 * label.
 */
export function LeadWizardLeadWarning({
  matches,
  onOpenExisting,
  onCreateAnyway
}: LeadWizardLeadWarningProps) {
  const { t } = useTranslation()

  if (!matches.length) return null

  const shown = matches.slice(0, MAX_VISIBLE_LEAD_MATCHES)
  const hasMore = matches.length > shown.length

  return (
    <LeadWizardMatchPanel
      title={t("leadWizard.leadDuplicate.title")}
      description={t("leadWizard.leadDuplicate.description")}
      more={
        hasMore
          ? t("leadWizard.leadDuplicate.more", {
              count: matches.length - shown.length
            })
          : undefined
      }
    >
      {shown.map((match, index) => (
        <Flex
          key={match.id ?? "unreachable-" + index}
          align={"center"}
          justify={"space-between"}
          gap={"small"}
          wrap
        >
          <Flex vertical>
            <Typography.Text strong>
              {match.lead_name ?? t("leadMatch.hiddenLead")}
            </Typography.Text>
            <Typography.Text type={"secondary"} className={"text-xs"}>
              {[
                matchStateLabel(match.match_state, t),
                match.owner_name &&
                  t("leadMatch.owner", { name: match.owner_name }),
                matchAgeLabel(match.created, t)
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography.Text>
          </Flex>

          <Flex align={"center"} gap={"small"}>
            <Tag color={REASON_COLOUR[match.reason]}>
              {t(REASON_LABEL[match.reason])}
            </Tag>
            {isReachable(match) && (
              <Popconfirm
                title={t("leadWizard.leadDuplicate.confirmTitle")}
                description={t("leadWizard.leadDuplicate.confirmBody")}
                okText={t("leadWizard.leadDuplicate.confirmOk")}
                cancelText={t("leadWizard.leadDuplicate.confirmCancel")}
                onConfirm={() => onOpenExisting(match)}
              >
                <Button size={"small"}>
                  {t("leadWizard.leadDuplicate.openExisting")}
                </Button>
              </Popconfirm>
            )}
            {/* Two ways out on every row and never a third: a row the
                      caller cannot open still offers this one. */}
            <Button size={"small"} type={"link"} onClick={onCreateAnyway}>
              {t("leadWizard.leadDuplicate.createAnyway")}
            </Button>
          </Flex>
        </Flex>
      ))}
    </LeadWizardMatchPanel>
  )
}
